import { useEffect, useRef, useState, useCallback } from "react";
import { Bell, Target, ShieldWarning, ArrowsLeftRight, DownloadSimple, Checks } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDateTime } from "../lib/format";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const TYPE_META = {
  sl_hit: { icon: ShieldWarning, cls: "text-loss" },
  tp_hit: { icon: Target, cls: "text-profit" },
  exchange_closed: { icon: ArrowsLeftRight, cls: "text-primary" },
  trade_imported: { icon: DownloadSimple, cls: "text-primary" },
};

const toastFor = (n) => {
  const opts = { description: n.message };
  if (n.type === "sl_hit") toast.error(n.title, opts);
  else if (n.type === "tp_hit") toast.success(n.title, opts);
  else toast.info(n.title, opts);
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const seenRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
      if (seenRef.current === null) {
        seenRef.current = new Set(data.items.map((n) => n.id));
        return;
      }
      for (const n of data.items) {
        if (!seenRef.current.has(n.id)) {
          seenRef.current.add(n.id);
          if (!n.read) toastFor(n);
        }
      }
    } catch {
      /* ignore poll errors */
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, 30000);
    return () => clearInterval(t);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read", {});
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      /* ignore */
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell-btn"
          className="relative flex items-center px-2 py-1.5 text-muted-foreground hover:text-foreground transition-colors duration-150 border border-border rounded-md"
          title="Notifications"
        >
          <Bell size={16} weight="bold" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-[10px] font-mono font-bold flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 bg-popover border-border" data-testid="notification-feed">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Notifications</span>
          {unread > 0 && (
            <button
              data-testid="mark-all-read-btn"
              onClick={markAllRead}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              <Checks size={14} /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              No notifications yet
            </div>
          ) : (
            items.map((n) => {
              const meta = TYPE_META[n.type] || TYPE_META.exchange_closed;
              const Icon = meta.icon;
              return (
                <div
                  key={n.id}
                  data-testid={`notification-item-${n.type}`}
                  className={`flex items-start gap-2.5 px-3 py-2.5 border-b border-border/50 last:border-b-0 ${
                    n.read ? "opacity-60" : ""
                  }`}
                >
                  <Icon size={16} weight="bold" className={`mt-0.5 shrink-0 ${meta.cls}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium leading-tight">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                    <div className="text-[10px] font-mono text-muted-foreground/60 mt-1">
                      {fmtDateTime(n.created_at)}
                    </div>
                  </div>
                  {!n.read && <span className="ml-auto mt-1 w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))] shrink-0" />}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
